import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Button from "components/atoms/Button";
import Spinner from "components/atoms/Spinner";
import { ConfirmManager } from "components/organisms/CommonUI/ConfirmPopup/ConfirmManager";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import { WaitingManager } from "components/organisms/CommonUI/WaitingPopup/WaitingManager";
import { log, LogLevel } from "utils/LogUtil";
import {
  listPharmacyAccess,
  suspendPharmacyAccess,
  unsuspendPharmacyAccess,
  getPharmacyCertUrl,
  verifyPharmacyCert,
  PharmacyAccessRow,
} from "api/pharmacyAccessService";

const LIMIT = 50;

/** 서류검증 클라이언트 필터 — 목록 API 파라미터에 없어 프론트에서 거른다. */
type CertFilter = "ALL" | "UNVERIFIED" | "VERIFIED";

/* ── styled ── */

const PageContainer = styled.div`
  padding: 24px;
  min-height: 100vh;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  margin: 0;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Select = styled.select`
  height: 36px;
  padding: 0 10px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 8px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  font-size: 14px;
`;

const PrivacyNote = styled.p`
  margin: 4px 0 16px;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

const Notice = styled.div`
  padding: 16px 20px;
  border: 1px solid ${COLOR_SYSTEM.get("Orange")?.[100]};
  border-radius: 12px;
  background-color: ${COLOR_SYSTEM.get("Orange")?.[10]};
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  font-size: 14px;
  line-height: 1.6;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 12px;
`;

const Table = styled.table`
  width: 100%;
  min-width: 1100px;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 14px;
  white-space: nowrap;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[10]};
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  font-weight: 600;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
`;

const Td = styled.td`
  padding: 12px 14px;
  vertical-align: top;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[15]};
`;

const EmptyRow = styled.td`
  padding: 48px 14px;
  text-align: center;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
`;

const AddressCell = styled.div`
  max-width: 240px;
  white-space: normal;
  word-break: break-word;
`;

const Muted = styled.span`
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
`;

type Tone = "green" | "red" | "orange" | "gray";
const TONE: Record<Tone, { bg?: string; fg?: string }> = {
  green: { bg: COLOR_SYSTEM.get("Green")?.[50], fg: COLOR_SYSTEM.get("Green")?.[800] },
  red: { bg: COLOR_SYSTEM.get("Red")?.[50], fg: COLOR_SYSTEM.get("Red")?.[800] },
  orange: { bg: COLOR_SYSTEM.get("Orange")?.[50], fg: COLOR_SYSTEM.get("Orange")?.[800] },
  gray: { bg: COLOR_SYSTEM.get("Gray")?.[50], fg: COLOR_SYSTEM.get("Gray")?.[600] },
};

const Badge = styled.span<{ $tone: Tone }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  background-color: ${({ $tone }) => TONE[$tone].bg ?? "#eee"};
  color: ${({ $tone }) => TONE[$tone].fg ?? "#333"};
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
`;

const PageInfo = styled.span`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
`;

/* ── 정지 사유 입력 폼 (ConfirmManager body로 렌더) ── */

const FormWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 320px;
`;

const FormLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
`;

const ReasonTextarea = styled.textarea`
  width: 100%;
  min-height: 96px;
  padding: 10px 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  box-sizing: border-box;
`;

const FormButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

interface SuspendReasonFormProps {
  pharmacyName: string;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * 정지 사유 입력 폼. ConfirmManager의 body로 렌더되며 자체 상태를 갖는다.
 * 사유 필수(계약/§4.2 UX) — 비어 있으면 [정지] 비활성.
 */
const SuspendReasonForm: React.FC<SuspendReasonFormProps> = ({
  pharmacyName,
  onCancel,
  onConfirm,
}) => {
  const [reason, setReason] = useState("");
  const trimmed = reason.trim();

  return (
    <FormWrap>
      <div>
        <strong>{pharmacyName || "이 약국"}</strong>의 데스크톱 앱 접근을 정지합니다.
        <br />
        정지되면 해당 약사는 게이트에서 차단됩니다. 사유를 입력해 주세요(필수).
      </div>
      <FormLabel htmlFor="suspend-reason">정지 사유</FormLabel>
      <ReasonTextarea
        id="suspend-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="예: 사업자등록증이 약국정보와 불일치"
        autoFocus
      />
      <FormButtons>
        <Button
          buttonType="button"
          color="Secondary"
          size="Medium"
          type="Text"
          variant="Ghost"
          onClick={onCancel}
        >
          취소
        </Button>
        <Button
          buttonType="button"
          color="Negative"
          size="Medium"
          type="Text"
          variant="Contained"
          disabled={trimmed.length === 0}
          disabledColor
          onClick={() => trimmed.length > 0 && onConfirm(trimmed)}
        >
          정지
        </Button>
      </FormButtons>
    </FormWrap>
  );
};

/* ── 유틸 ── */

const formatDate = (iso: string | null): string => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" });
};

const isSuspended = (row: PharmacyAccessRow): boolean =>
  row.status === "SUSPENDED" || !!row.suspendedAt;

/* ── 페이지 ── */

const PharmacyAccess: React.FC = () => {
  const [rows, setRows] = useState<PharmacyAccessRow[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>(""); // "" = 전체
  const [certFilter, setCertFilter] = useState<CertFilter>("ALL");
  const [page, setPage] = useState(1); // 1-based
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [notImplemented, setNotImplemented] = useState(false);

  // 조치(정지/해제/검증) 동시 실행 방지 — 정적 노드(Confirm 버튼)라 상태로 못 막으므로 ref 가드.
  const busyRef = useRef(false);

  const loadRows = useCallback(
    async (pageToLoad: number, status: string) => {
      setIsLoading(true);
      try {
        const res = await listPharmacyAccess({
          status: status || undefined,
          limit: LIMIT,
          offset: (pageToLoad - 1) * LIMIT,
        });

        if (res.error === 0 && res.data) {
          setRows(res.data.items);
          setTotal(res.data.total);
          setHasMore(res.data.items.length === LIMIT);
          setNotImplemented(false);
          setPage(pageToLoad);
        } else if (res.error === 404) {
          // 백엔드 admin API(P1-d) 미구현 — 화면은 죽지 않고 안내만 띄운다.
          setRows([]);
          setTotal(undefined);
          setHasMore(false);
          setNotImplemented(true);
          setPage(pageToLoad);
        } else {
          setRows([]);
          setHasMore(false);
          setNotImplemented(false);
          ToastManager.show({ content: res.message || "목록을 불러오지 못했습니다." });
        }
      } catch (err) {
        log(LogLevel.ERROR, "약국 가입 목록 로드 오류:", err);
        ToastManager.show({ content: "목록을 불러오지 못했습니다." });
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadRows(1, statusFilter);
  }, [statusFilter, loadRows]);

  const refresh = useCallback(() => {
    loadRows(page, statusFilter);
  }, [loadRows, page, statusFilter]);

  // 서류검증 필터 + "미검증 먼저" 정렬(운영자 작업 큐, spec §4.2 D14). 정렬은 클라이언트 몫.
  const displayRows = useMemo(() => {
    let r = rows;
    if (certFilter === "UNVERIFIED") r = r.filter((x) => !x.certVerifiedAt);
    else if (certFilter === "VERIFIED") r = r.filter((x) => !!x.certVerifiedAt);
    return [...r].sort((a, b) => (a.certVerifiedAt ? 1 : 0) - (b.certVerifiedAt ? 1 : 0));
  }, [rows, certFilter]);

  const runExclusive = useCallback(async (fn: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      await fn();
    } finally {
      busyRef.current = false;
    }
  }, []);

  /** 사업자등록증 보기 — 클릭 시에만 서명 URL 발급 후 새 창으로 연다(썸네일 미표시, 계약 §4.6). */
  const handleViewCert = useCallback(async (row: PharmacyAccessRow) => {
    // await 뒤 window.open은 팝업 차단될 수 있어, 클릭 제스처 내에서 빈 창을 먼저 연다.
    // ⚠️ features에 noopener를 주면 반환 핸들이 null이 되어 location을 세팅할 수 없다 —
    //    핸들을 유지하되 opener 참조만 끊는다(우리 S3 서명 URL이라 탭재킹 위험은 낮다).
    const win = window.open("", "_blank");
    if (win) win.opener = null;
    const res = await getPharmacyCertUrl(row.userId);
    if (res.error === 0 && res.data?.url) {
      if (win) win.location.href = res.data.url;
      else ToastManager.show({ content: "팝업이 차단되었습니다. 팝업 허용 후 다시 시도해 주세요." });
    } else {
      if (win) win.close();
      const msg =
        res.error === 404
          ? "백엔드 admin API가 아직 구현되지 않았습니다."
          : res.message || "사업자등록증을 여는 데 실패했습니다.";
      ToastManager.show({ content: msg });
    }
  }, []);

  const performVerify = useCallback(
    (row: PharmacyAccessRow) =>
      runExclusive(async () => {
        WaitingManager.show({ message: "검증 완료 처리 중입니다..." });
        try {
          const res = await verifyPharmacyCert(row.userId);
          if (res.error === 0) {
            ToastManager.show({ content: "검증 완료로 표시했습니다." });
            refresh();
          } else {
            ToastManager.show({
              content:
                res.error === 404
                  ? "백엔드 admin API가 아직 구현되지 않았습니다."
                  : res.message || "검증 완료 처리에 실패했습니다.",
            });
          }
        } finally {
          WaitingManager.hide();
        }
      }),
    [runExclusive, refresh]
  );

  const handleVerify = useCallback(
    (row: PharmacyAccessRow) => {
      ConfirmManager.show({
        title: "서류 검증 완료",
        body: `${row.pharmacyName || "이 약국"}의 사업자등록증을 확인하셨습니까? 검증 완료는 표식일 뿐 기능에는 영향을 주지 않습니다.`,
        buttons: [
          <Button
            key="cancel"
            buttonType="button"
            color="Secondary"
            size="Medium"
            type="Text"
            variant="Ghost"
            onClick={() => ConfirmManager.hide()}
          >
            취소
          </Button>,
          <Button
            key="confirm"
            buttonType="button"
            color="Primary"
            size="Medium"
            type="Text"
            variant="Contained"
            onClick={() => {
              ConfirmManager.hide();
              performVerify(row);
            }}
          >
            검증 완료
          </Button>,
        ],
      });
    },
    [performVerify]
  );

  const performSuspend = useCallback(
    (row: PharmacyAccessRow, reason: string) =>
      runExclusive(async () => {
        WaitingManager.show({ message: "정지 처리 중입니다..." });
        try {
          const res = await suspendPharmacyAccess(row.userId, reason);
          if (res.error === 0) {
            ToastManager.show({ content: "정지 처리했습니다." });
            refresh();
          } else {
            ToastManager.show({
              content:
                res.error === 404
                  ? "백엔드 admin API가 아직 구현되지 않았습니다."
                  : res.message || "정지 처리에 실패했습니다.",
            });
          }
        } finally {
          WaitingManager.hide();
        }
      }),
    [runExclusive, refresh]
  );

  const handleSuspend = useCallback(
    (row: PharmacyAccessRow) => {
      ConfirmManager.show({
        title: "약국 접근 정지",
        body: (
          <SuspendReasonForm
            pharmacyName={row.pharmacyName || ""}
            onCancel={() => ConfirmManager.hide()}
            onConfirm={(reason) => {
              ConfirmManager.hide();
              performSuspend(row, reason);
            }}
          />
        ),
      });
    },
    [performSuspend]
  );

  const performUnsuspend = useCallback(
    (row: PharmacyAccessRow) =>
      runExclusive(async () => {
        WaitingManager.show({ message: "정지 해제 처리 중입니다..." });
        try {
          const res = await unsuspendPharmacyAccess(row.userId);
          if (res.error === 0) {
            ToastManager.show({ content: "정지를 해제했습니다." });
            refresh();
          } else {
            ToastManager.show({
              content:
                res.error === 404
                  ? "백엔드 admin API가 아직 구현되지 않았습니다."
                  : res.message || "정지 해제에 실패했습니다.",
            });
          }
        } finally {
          WaitingManager.hide();
        }
      }),
    [runExclusive, refresh]
  );

  const handleUnsuspend = useCallback(
    (row: PharmacyAccessRow) => {
      ConfirmManager.show({
        title: "정지 해제",
        body: `${row.pharmacyName || "이 약국"}의 정지를 해제하시겠습니까?`,
        buttons: [
          <Button
            key="cancel"
            buttonType="button"
            color="Secondary"
            size="Medium"
            type="Text"
            variant="Ghost"
            onClick={() => ConfirmManager.hide()}
          >
            취소
          </Button>,
          <Button
            key="confirm"
            buttonType="button"
            color="Primary"
            size="Medium"
            type="Text"
            variant="Contained"
            onClick={() => {
              ConfirmManager.hide();
              performUnsuspend(row);
            }}
          >
            정지 해제
          </Button>,
        ],
      });
    },
    [performUnsuspend]
  );

  return (
    <PageContainer>
      <HeaderRow>
        <Title>PhAI 약국 가입 관리</Title>
        <Controls>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="상태 필터"
          >
            <option value="">상태: 전체</option>
            <option value="APPROVED">정상</option>
            <option value="SUSPENDED">정지</option>
          </Select>
          <Select
            value={certFilter}
            onChange={(e) => setCertFilter(e.target.value as CertFilter)}
            aria-label="서류검증 필터"
          >
            <option value="ALL">서류검증: 전체</option>
            <option value="UNVERIFIED">미검증만</option>
            <option value="VERIFIED">검증완료만</option>
          </Select>
          <Button
            buttonType="button"
            color="Secondary"
            size="Medium"
            type="Text"
            variant="Outlined"
            onClick={refresh}
          >
            새로고침
          </Button>
        </Controls>
      </HeaderRow>

      <PrivacyNote>
        ⚠️ 사업자등록증에는 대표자 성명 등 개인정보가 포함됩니다. 썸네일은 표시하지 않으며,
        [서류 보기]를 누를 때만 단기 서명 URL로 새 창에서 열립니다. 미검증 건이 위쪽에 먼저
        보입니다(운영 작업 큐).
      </PrivacyNote>

      {isLoading ? (
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      ) : notImplemented ? (
        <Notice>
          백엔드 약국 가입 관리 API(<code>{"GET /api/v1/products/pharmacy/admin/access"}</code>)가
          아직 구현되지 않았습니다(404). API(P1-d)가 배포되면 이 화면에서 가입 약국 목록과
          정지·검증 조치를 사용할 수 있습니다.
        </Notice>
      ) : (
        <>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>약국명</Th>
                  <Th>사업자번호</Th>
                  <Th>우편번호·주소</Th>
                  <Th>가입일</Th>
                  <Th>약관 버전</Th>
                  <Th>상태</Th>
                  <Th>서류검증</Th>
                  <Th>사용자</Th>
                  <Th>조치</Th>
                </tr>
              </thead>
              <tbody>
                {displayRows.length === 0 ? (
                  <tr>
                    <EmptyRow colSpan={9}>가입한 약국이 없습니다.</EmptyRow>
                  </tr>
                ) : (
                  displayRows.map((row) => {
                    const suspended = isSuspended(row);
                    const verified = !!row.certVerifiedAt;
                    return (
                      <tr key={row.userId}>
                        <Td>{row.pharmacyName || <Muted>-</Muted>}</Td>
                        <Td>{row.bizRegNo || <Muted>-</Muted>}</Td>
                        <Td>
                          <AddressCell>
                            {row.postalCode ? `(${row.postalCode}) ` : ""}
                            {row.address || <Muted>-</Muted>}
                            {row.addressDetail ? ` ${row.addressDetail}` : ""}
                          </AddressCell>
                        </Td>
                        <Td>{formatDate(row.joinedAt)}</Td>
                        <Td>{row.agreementVersion || <Muted>-</Muted>}</Td>
                        <Td>
                          {suspended ? (
                            <Badge $tone="red" title={row.suspendReason || undefined}>
                              정지
                            </Badge>
                          ) : (
                            <Badge $tone="green">정상</Badge>
                          )}
                        </Td>
                        <Td>
                          {verified ? (
                            <Badge $tone="gray">검증완료</Badge>
                          ) : (
                            <Badge $tone="orange">미검증</Badge>
                          )}
                        </Td>
                        <Td>
                          <div>{row.user.email || <Muted>-</Muted>}</div>
                          <Muted>{row.user.name || ""}</Muted>
                        </Td>
                        <Td>
                          <Actions>
                            <Button
                              buttonType="button"
                              color="Secondary"
                              size="XSmall"
                              type="Text"
                              variant="Outlined"
                              onClick={() => handleViewCert(row)}
                            >
                              서류 보기
                            </Button>
                            {!verified && (
                              <Button
                                buttonType="button"
                                color="Primary"
                                size="XSmall"
                                type="Text"
                                variant="Tinted"
                                onClick={() => handleVerify(row)}
                              >
                                검증 완료
                              </Button>
                            )}
                            {suspended ? (
                              <Button
                                buttonType="button"
                                color="Primary"
                                size="XSmall"
                                type="Text"
                                variant="Ghost"
                                onClick={() => handleUnsuspend(row)}
                              >
                                정지 해제
                              </Button>
                            ) : (
                              <Button
                                buttonType="button"
                                color="Negative"
                                size="XSmall"
                                type="Text"
                                variant="Ghost"
                                onClick={() => handleSuspend(row)}
                              >
                                정지
                              </Button>
                            )}
                          </Actions>
                        </Td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </TableWrap>

          <Pagination>
            <PageInfo>
              {page}페이지
              {typeof total === "number" ? ` · 전체 ${total}건` : ""}
              {certFilter !== "ALL" ? ` · 이 페이지 ${displayRows.length}건 표시` : ""}
            </PageInfo>
            <Button
              buttonType="button"
              color="Secondary"
              size="Small"
              type="Text"
              variant="Outlined"
              disabled={page <= 1}
              disabledColor
              onClick={() => loadRows(page - 1, statusFilter)}
            >
              이전
            </Button>
            <Button
              buttonType="button"
              color="Secondary"
              size="Small"
              type="Text"
              variant="Outlined"
              disabled={!hasMore}
              disabledColor
              onClick={() => loadRows(page + 1, statusFilter)}
            >
              다음
            </Button>
          </Pagination>
        </>
      )}
    </PageContainer>
  );
};

export default PharmacyAccess;
